import { apiGet, apiPostJson } from './aiApi';
import { buildApiUrl, getApiHeaders } from './queriesHelpers';
import type {
  AIContentRecord as CanonicalAIContentRecord,
  AIResponse,
} from '../types/aiTypes';

export type AIContentRecord = CanonicalAIContentRecord;

/**
 * Compatibility return type for the AIResponse -> agent history migration.
 *
 * It is intentionally assignable to both:
 * - legacy AIResponse consumers
 * - canonical AIContentRecord consumers from types/aiTypes
 */
export type AIContentCompat = AIResponse & CanonicalAIContentRecord & Record<string, any>;

type ID = string | number;

type FetchOrCreateOptions = {
  session_id?: string;
  sessionId?: string;
  conversation_id?: string | number | null;
  message_id?: string | number | null;

  model?: string;
  model_name?: string;
  modelName?: string;
  llm_model?: string;
  llmModel?: string;

  use_rag?: boolean;
  use_drift?: boolean;
  useDrift?: boolean;
  drift_enabled?: boolean;

  mcp_enabled?: boolean;
  useMcp?: boolean;
  mcp?: {
    enabled?: boolean;
    [key: string]: any;
  };

  rag_profile_id?: number | null;
  pipeline_group_id?: number | null;
  rag_pipeline_group_id?: number | null;

  prompt?: string;
  title?: string;
  metadata?: Record<string, any>;

  [key: string]: any;
};

type FeedbackPayload = {
  related_type?: string | null;
  related_id?: string | number | null;
  score?: any;
  text?: string;
  source?: string;
  metadata?: Record<string, any>;
  feedback?: any;
  message_id?: string | number | null;
  agent_message?: string | number | null;
  conversation?: string | number | null;
  session_id?: string | null;
  [key: string]: any;
};

function valueOf<T = any>(...values: any[]): T | null {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== '') return value as T;
  }

  return null;
}

function stringId(value: any): string | null {
  if (value === undefined || value === null || value === '') return null;
  return String(value);
}

/**
 * question_id is only resolvable server-side when it addresses an ai_question
 * row, which requires a numeric id. Section keys and resource types also flow
 * through questionId, and those still need a prompt sent with the request.
 */
function numericQuestionId(value: any): number | null {
  if (value === undefined || value === null || value === '') return null;

  const numeric = Number(value);

  return Number.isFinite(numeric) ? numeric : null;
}

function normalizeEndpoint(endpoint: string) {
  return String(endpoint || '').replace(/^\/+/, '');
}

function safeJsonParse(value: any): any {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'object') return value;

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;

    try {
      return JSON.parse(trimmed);
    } catch {
      return value;
    }
  }

  return value;
}

function stringifyValue(value: any, fallback = ''): string {
  if (value === null || value === undefined || value === '') return fallback;
  if (typeof value === 'string') return value;

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function normalizeItems(data: any): any[] {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.records)) return data.records;
  if (Array.isArray(data.results)) return data.results;
  if (Array.isArray(data.messages)) return data.messages;
  if (Array.isArray(data.conversations)) return data.conversations;
  return [];
}

async function tryGet(endpoint: string, params?: Record<string, any>) {
  try {
    return await apiGet<any>(normalizeEndpoint(endpoint), params || {});
  } catch {
    return null;
  }
}

async function tryPost(endpoint: string, payload?: Record<string, any>) {
  try {
    return await apiPostJson<any>(normalizeEndpoint(endpoint), payload || {});
  } catch {
    return null;
  }
}

async function apiPatchJson<T = any>(endpoint: string, payload: any): Promise<T> {
  const normalized = normalizeEndpoint(endpoint);
  const url = buildApiUrl(normalized);

  const response = await fetch(url, {
    method: 'PATCH',
    credentials: 'include',
    headers: getApiHeaders(null, 'PATCH', normalized),
    body: JSON.stringify(payload || {}),
  });

  if (!response.ok) {
    let message = `PATCH ${url} failed with status ${response.status}`;

    try {
      const data = await response.json();
      message = data?.detail || data?.message || data?.error || message;
    } catch {
      try {
        const text = await response.text();
        if (text) message = text;
      } catch {
        // ignore
      }
    }

    throw new Error(message);
  }

  try {
    return await response.json();
  } catch {
    return {} as T;
  }
}

function firstAssistantMessage(messages: any[]) {
  return (
    messages.find((message) => String(message?.role || '').toLowerCase() === 'assistant') ||
    messages.find((message) => message?.content || message?.response || message?.response_text) ||
    null
  );
}

function newestByUpdatedAt(items: any[]) {
  if (!Array.isArray(items) || items.length <= 1) {
    return items?.[0] || null;
  }

  return [...items].sort((a, b) => {
    const aTime = new Date(a?.last_updated || a?.updated_at || a?.updated || a?.created_at || a?.created || 0).getTime();
    const bTime = new Date(b?.last_updated || b?.updated_at || b?.updated || b?.created_at || b?.created || 0).getTime();
    return bTime - aTime;
  })[0] || null;
}

/**
 * valueOf() treats '' as absent and returns null when nothing matches, so a
 * trailing '' sentinel never applies. Coerce here instead: String(null) would
 * otherwise render the literal text "null" in the AI content panel.
 *
 * Also drop already-persisted "null"/"undefined" text so a poisoned saved row
 * is treated as empty and regenerates instead of rendering the word.
 */
function coerceExtractedText(value: any): string {
  if (value === undefined || value === null) return '';

  const text = String(value).trim();

  if (!text || text === 'null' || text === 'undefined') return '';

  return String(value);
}

function extractContent(raw: any): string {
  if (!raw) return '';

  const metadata = safeJsonParse(raw.metadata) || {};
  const responseMetadata = safeJsonParse(raw.response_metadata) || {};
  const conversation = safeJsonParse(raw.conversation) || {};

  return coerceExtractedText(
    valueOf(
      raw.response,
      raw.response_text,
      raw.content,
      raw.answer,
      raw.text,
      raw.message,
      raw.message_content,
      raw.assistant_message?.content,
      raw.assistant_message?.response,
      raw.assistant?.content,
      raw.data?.response,
      raw.data?.answer,
      raw.data?.content,
      metadata.response,
      metadata.content,
      responseMetadata.response,
      responseMetadata.content,
      conversation.response,
      conversation.content,
      conversation.metadata?.response,
      conversation.metadata?.content,
    ),
  );
}

function extractPrompt(raw: any): string {
  if (!raw) return '';

  const metadata = safeJsonParse(raw.metadata) || {};
  const conversation = safeJsonParse(raw.conversation) || {};

  return coerceExtractedText(
    valueOf(
      raw.prompt_text,
      raw.request_text,
      raw.prompt,
      raw.question_text,
      raw.question_text,
      raw.question?.question,
      raw.question?.title,
      raw.question,
      raw.user_message?.content,
      raw.user?.content,
      raw.data?.prompt,
      metadata.prompt,
      metadata.request,
      conversation.prompt_text,
      conversation.request_text,
      conversation.messages?.find?.((m: any) => String(m?.role || '').toLowerCase() === 'user')?.content,
    ),
  );
}

function extractAssistantMessageId(raw: any): string | number | null {
  if (!raw) return null;

  const metadata = safeJsonParse(raw.metadata) || {};
  const conversation = safeJsonParse(raw.conversation) || {};

  return valueOf(
    raw.assistant_message_id,
    raw.message_id,
    raw.message?.id,
    raw.assistant_message?.id,
    raw.assistant?.id,
    metadata.assistant_message_id,
    metadata.message_id,
    conversation.assistant_message_id,
    conversation.message_id,
    raw.id,
  );
}

function extractConversationId(raw: any): string | number | null {
  if (!raw) return null;

  const metadata = safeJsonParse(raw.metadata) || {};
  const conversation = safeJsonParse(raw.conversation) || {};

  return valueOf(
    raw.conversation_id,
    raw.conversation?.id,
    raw.session?.conversation_id,
    raw.data?.conversation_id,
    metadata.conversation_id,
    conversation.conversation_id,
    conversation.session_id,
  );
}

function extractSessionId(raw: any): string | null {
  if (!raw) return null;

  const metadata = safeJsonParse(raw.metadata) || {};
  const conversation = safeJsonParse(raw.conversation) || {};

  return valueOf<string>(
    raw.session_id,
    raw.sessionId,
    raw.conversation?.session_id,
    raw.session?.session_id,
    raw.data?.session_id,
    metadata.session_id,
    conversation.session_id,
  );
}

function extractRag(raw: any): any {
  if (!raw) return null;

  const metadata = safeJsonParse(raw.metadata) || {};
  const responseMetadata = safeJsonParse(raw.response_metadata) || {};
  const conversation = safeJsonParse(raw.conversation) || {};
  const conversationMetadata = safeJsonParse(conversation.metadata) || {};

  return valueOf(
    raw.rag,
    raw.rag_context,
    raw.rag_context_json,
    raw.rag_chunks,
    raw.rag_chunks_json,
    raw.retrieval?.rag,
    raw.retrieval?.context,
    raw.data?.rag,
    raw.data?.metadata?.rag,
    metadata.rag,
    metadata.rag_context,
    metadata.rag_chunks,
    responseMetadata.rag,
    responseMetadata.rag_context,
    responseMetadata.rag_chunks,
    conversation.rag,
    conversationMetadata.rag,
    null,
  );
}

export function buildAIContentSessionId(
  entityType?: string | null,
  entityId?: ID | null,
  questionId?: ID | null,
  scope?: string | null,
): string {
  const cleanEntityType = String(entityType || 'unknown').trim() || 'unknown';
  const cleanEntityId = String(entityId || 'unknown').trim() || 'unknown';
  const cleanQuestionId = String(questionId || 'default').trim() || 'default';
  const cleanScope = String(scope || 'ai-content').trim() || 'ai-content';

  return `${cleanScope}:${cleanEntityType}:${cleanEntityId}:${cleanQuestionId}`;
}

function buildLegacyQuestion(value: any, title?: string | null) {
  if (value && typeof value === 'object') return value;

  return {
    id: value === undefined || value === null || value === '' ? 'default' : String(value),
    title: title || 'AI Content',
    question: title || 'AI Content',
    name: title || 'AI Content',
  };
}

export function toAIContentCompat(raw: any, fallback: Record<string, any> = {}): AIContentCompat {
  const source = raw || {};
  const metadata = safeJsonParse(source.metadata) || {};
  const responseMetadata = safeJsonParse(source.response_metadata) || {};
  const promptInputs = safeJsonParse(source.prompt_inputs) || {};
  const conversation = safeJsonParse(source.conversation) || {};
  const conversationMetadata = safeJsonParse(conversation.metadata) || {};

  const entityType = valueOf<string>(
    source.entity_type,
    source.related_type,
    metadata.entity_type,
    metadata.related_type,
    conversationMetadata.entity_type,
    conversationMetadata.related_type,
    promptInputs.entity_type,
    fallback.entity_type,
    fallback.related_type,
    'ai-content',
  );

  const entityId = valueOf<ID>(
    source.entity_id,
    source.related_id,
    metadata.entity_id,
    metadata.related_id,
    conversationMetadata.entity_id,
    conversationMetadata.related_id,
    promptInputs.entity_id,
    fallback.entity_id,
    fallback.related_id,
    'default',
  );

  const questionId = valueOf<string | number>(
    source.question_id,
    source.section_key,
    source.resource_type,
    metadata.question_id,
    conversationMetadata.question_id,
    promptInputs.question_id,
    promptInputs.resource_type,
    fallback.question_id,
    source.question && typeof source.question !== 'object' ? source.question : null,
    'default',
  );

  const title = valueOf<string>(
    source.title,
    metadata.title,
    conversationMetadata.title,
    fallback.title,
    source.question_title,
    source.resource_type,
    source.section_key,
    'AI Content',
  );

  const sessionId =
    valueOf<string>(
      extractSessionId(source),
      fallback.session_id,
      buildAIContentSessionId(entityType, entityId, questionId),
    ) || buildAIContentSessionId(entityType, entityId, questionId);

  const rawConversationId = valueOf(
    extractConversationId(source),
    fallback.conversation_id,
    sessionId,
  );

  const rawMessageId = valueOf(
    extractAssistantMessageId(source),
    fallback.message_id,
    fallback.assistant_message_id,
    null,
  );

  const conversationId = stringId(rawConversationId);
  const messageId = stringId(rawMessageId);

  const response = valueOf<string>(
    extractContent(source),
    fallback.response,
    fallback.content,
    fallback.response_text,
    '',
  ) || '';

  const prompt = valueOf<string>(
    extractPrompt(source),
    fallback.prompt_text,
    fallback.request_text,
    '',
  ) || '';

  /**
   * Keep id string-compatible for AIResponse.
   */
  const id = String(
    valueOf(
      source.id,
      source.ai_response_id,
      source.response_id,
      metadata.ai_response_id,
      conversationMetadata.ai_response_id,
      rawMessageId,
      rawConversationId,
      fallback.id,
      sessionId,
    ) || sessionId,
  );

  const status = String(
    valueOf(
      source.status,
      source.state,
      source.finish_reason ? 'complete' : null,
      response ? 'complete' : null,
      fallback.status,
      'complete',
    ),
  );

  const rag = valueOf(
    extractRag(source),
    fallback.rag,
    null,
  );

  const relatedType = String(valueOf(source.related_type, entityType, fallback.related_type, 'ai-content'));
  const relatedId = valueOf(source.related_id, entityId, fallback.related_id, 'default');

  const compat: Record<string, any> = {
    ...fallback,
    ...source,

    /**
     * Fields required by legacy AIResponse.
     */
    id,
    question: buildLegacyQuestion(source.question || questionId, title),
    status,
    response,

    /**
     * Fields required by canonical AIContentRecord / new history flow.
     */
    session_id: sessionId,
    conversation_id: conversationId,
    message_id: messageId,
    assistant_message_id: stringId(valueOf(source.assistant_message_id, rawMessageId, fallback.assistant_message_id)),
    user_message_id: stringId(valueOf(source.user_message_id, source.user_message?.id, conversation.user_message_id, fallback.user_message_id)),

    entity_type: entityType,
    entity_id: entityId,
    related_type: relatedType,
    related_id: relatedId,

    question_id: questionId,

    content: response,
    response_text: valueOf(coerceExtractedText(source.response_text), response, fallback.response_text),
    prompt_text: valueOf(source.prompt_text, prompt, fallback.prompt_text),
    request_text: valueOf(source.request_text, prompt, fallback.request_text),

    rag,
    feedback: valueOf(source.feedback, metadata.feedback, conversationMetadata.feedback, fallback.feedback),
    metadata: {
      ...metadata,
      ...responseMetadata,
      ...conversationMetadata,
      ...(fallback.metadata || {}),
      session_id: sessionId,
      conversation_id: conversationId,
      message_id: messageId,
      entity_type: entityType,
      entity_id: entityId,
      related_type: relatedType,
      related_id: relatedId,
      question_id: questionId,
      title,
      rag: rag || metadata.rag || conversationMetadata.rag || undefined,
    },

    created_at: valueOf(source.created_at, source.created, fallback.created_at),
    updated_at: valueOf(source.updated_at, source.last_updated, source.updated, source.modified, fallback.updated_at),

    raw: source,
  };

  return compat as AIContentCompat;
}

function normalizeHistoryResponse(data: any, fallback: Record<string, any>) {
  if (!data) return null;

  /**
   * Preferred backend shape from /api/ai/history/.
   *
   * This is the canonical saved AIContent row. Prefer it over messages because
   * AIContent may not persist normal agent messages, and ai_response is the
   * actual saved record.
   */
  if (data.ai_response) {
    return toAIContentCompat(
      {
        ...data.ai_response,
        session_id: data.ai_response.session_id || data.session_id || fallback.session_id,
        conversation_id: data.ai_response.conversation_id || data.conversation_id || data.session_id || fallback.session_id,
        metadata: {
          ...(data.ai_response.metadata || {}),
          ...(data.metadata || {}),
          ai_response_id: data.ai_response.id,
          session_id: data.ai_response.session_id || data.session_id || fallback.session_id,
          conversation_id: data.ai_response.conversation_id || data.conversation_id || data.session_id || fallback.session_id,
        },
      },
      fallback,
    );
  }

  /**
   * Direct saved AI response endpoint and chat/saved endpoint:
   * {
   *   response,
   *   metadata,
   *   ...
   * }
   */
  const directContent = extractContent(data);
  if (directContent || data.id || data.message_id || data.conversation_id || data.ai_response_id) {
    return toAIContentCompat(data, fallback);
  }

  /**
   * Agent conversation history endpoint:
   * {
   *   session_id,
   *   count,
   *   messages: [...]
   * }
   */
  if (Array.isArray(data.messages) && data.messages.length) {
    const assistant = firstAssistantMessage(data.messages);

    if (assistant) {
      return toAIContentCompat(
        {
          ...assistant,
          conversation_id: data.id || data.conversation_id || data.session_id,
          session_id: data.session_id || fallback.session_id,
          metadata: {
            ...(assistant.metadata || {}),
            session_id: data.session_id || fallback.session_id,
            conversation_id: data.id || data.conversation_id || data.session_id,
          },
        },
        fallback,
      );
    }

    return toAIContentCompat(
      {
        ...data.messages[data.messages.length - 1],
        conversation_id: data.id || data.conversation_id || data.session_id,
        session_id: data.session_id || fallback.session_id,
      },
      fallback,
    );
  }

  const rows = normalizeItems(data);

  if (rows.length) {
    const assistant = firstAssistantMessage(rows);
    if (assistant) {
      return toAIContentCompat(assistant, fallback);
    }

    return toAIContentCompat(newestByUpdatedAt(rows), fallback);
  }

  return null;
}

export async function fetchAIResponse(
  entityType: string,
  entityId: ID,
  questionId?: ID | null,
  context?: string,
  options: FetchOrCreateOptions = {},
): Promise<AIContentCompat | null> {
  const sessionId =
    options.session_id ||
    options.sessionId ||
    buildAIContentSessionId(entityType, entityId, questionId);

  const fallback: Record<string, any> = {
    id: sessionId,
    session_id: sessionId,
    conversation_id: sessionId,
    entity_type: entityType,
    entity_id: entityId,
    related_type: entityType,
    related_id: entityId,
    question: buildLegacyQuestion(questionId, options.title),
    question_id: questionId || null,
    prompt_text: context || '',
    request_text: context || '',
    status: 'complete',
  };

  const savedParams = {
    related_type: entityType,
    related_id: entityId,
    entity_type: entityType,
    entity_id: entityId,
    question_id: questionId || undefined,
    session_id: sessionId,
    detailed: true,
    depth: 2,
    limit: 50,
    size: 50,
  };

  const attempts = [
    /**
     * This is the endpoint your browser shows returning 200 OK.
     */
    await tryGet('ai/history/', savedParams),

    /**
     * Direct saved response endpoint: GET /api/ai/chat/saved/.
     *
     * Only this path is real. The former 'chat/saved/' variants resolved to
     * /api/chat/saved/, which no AI route serves — one of them answered 200 with
     * an unrelated body that this loop would then accept as saved AI content.
     */
    await tryGet('ai/chat/saved/', {
      related_type: entityType,
      related_id: entityId,
      question_id: questionId || undefined,
      session_id: sessionId,
    }),

    /**
     * toolbox_db generic ai_response fallback.
     */
    await tryGet('db/ai-response/', {
      related_type: entityType,
      related_id: entityId,
      question: questionId || undefined,
      question_id: questionId || undefined,
      detailed: true,
      depth: 2,
      size: 5,
      'order-by': '-last_updated',
    }),

    /**
     * Agent conversation fallbacks.
     */
    await tryGet('db/agent-conversation/history/', {
      session_id: sessionId,
      detailed: true,
      depth: 2,
      limit: 50,
      size: 50,
    }),
    await tryGet('db/agent-conversation/session/', {
      session_id: sessionId,
      detailed: true,
      depth: 2,
      limit: 50,
      size: 50,
    }),
    await tryGet('db/agent-conversation/', {
      session_id: sessionId,
      detailed: true,
      depth: 2,
      size: 1,
    }),
  ];

  for (const attempt of attempts) {
    const normalized = normalizeHistoryResponse(attempt, fallback);

    if (normalized?.response || normalized?.content || normalized?.response_text) {
      return normalized;
    }
  }

  return null;
}

function buildChatPayload(input: {
  entityType?: string | null;
  entityId?: ID | null;
  questionId?: ID | null;
  context?: string;
  prompt?: string;
  tags?: any[];
  source?: any;
  options?: FetchOrCreateOptions;
}) {
  const {
    entityType,
    entityId,
    questionId,
    context,
    prompt,
    tags,
    source,
    options = {},
  } = input;

  const sessionId =
    options.session_id ||
    options.sessionId ||
    buildAIContentSessionId(entityType, entityId, questionId);

  const modelName =
    options.model ||
    options.model_name ||
    options.modelName ||
    options.llm_model ||
    options.llmModel;

  const useDrift =
    options.use_rag ??
    options.use_drift ??
    options.useDrift ??
    options.drift_enabled ??
    true;

  const useMcp =
    options.mcp?.enabled ??
    options.mcp_enabled ??
    options.useMcp ??
    false;

  const pipelineGroupId =
    options.rag_pipeline_group_id ??
    options.pipeline_group_id ??
    options.rag_profile_id ??
    null;

  /**
   * With a numeric question_id the backend loads the stored question text
   * (_resolve_prompt_text in app/api/routes/chat.py), so send no prompt and let
   * it do that. Falling back to the context blob or a placeholder made *that*
   * the question the model answered, and the real question was never asked.
   */
  const finalPrompt =
    prompt ||
    options.prompt ||
    (numericQuestionId(questionId) !== null
      ? ''
      : context ||
        (typeof questionId === 'string' ? questionId : '') ||
        'Generate AI content for the selected entity.');

  const metadata = {
    ...(options.metadata || {}),
    source: options.metadata?.source || source || 'AIContent',
    tags: tags || [],
    title: options.title,
    entity_type: entityType,
    entity_id: entityId,
    related_type: entityType,
    related_id: entityId,
    question_id: questionId,
    regenerate: Boolean(options.regenerate),
    compatibility_mode: 'AIResponse',
  };

  return {
    session_id: sessionId,
    conversation_id: stringId(options.conversation_id) || sessionId,

    ...(finalPrompt
      ? {
          message: finalPrompt,
          prompt: finalPrompt,
          question: finalPrompt,
        }
      : {}),

    related_type: entityType,
    related_id: entityId,
    entity_type: entityType,
    entity_id: entityId,
    question_id: questionId,

    context,
    prompt_context: context,

    use_drift: useDrift,
    useDrift,
    drift_enabled: useDrift,
    use_rag: useDrift,

    mcp_enabled: useMcp,
    useMcp,
    use_mcp: useMcp,

    model: modelName,
    model_name: modelName,
    modelName,
    llm_model: modelName,
    llmModel: modelName,

    rag_pipeline_group_id: pipelineGroupId,
    pipeline_group_id: pipelineGroupId,
    rag_profile_id: pipelineGroupId,

    metadata,
  };
}

function parseCreateArgs(args: any[]) {
  let entityType: string | undefined;
  let entityId: ID | null | undefined;
  let questionId: ID | null | undefined;
  let context = '';
  let prompt = '';
  let tags: any[] = [];
  let source: any = null;
  let regenerate = false;
  let options: FetchOrCreateOptions = {};

  if (args.length === 1 && args[0] && typeof args[0] === 'object') {
    const payload = args[0];

    entityType = payload.entity_type || payload.related_type || payload.type;
    entityId = payload.entity_id || payload.related_id || payload.id || null;
    questionId = payload.question_id || payload.section_key || payload.resource_type || payload.question_id || null;
    context = payload.context || payload.ai_context || payload.prompt_context || payload.response || '';
    prompt = payload.prompt || payload.message || payload.question_text || payload.request_text || '';
    tags = Array.isArray(payload.tags) ? payload.tags : [];
    source = payload.source || payload.entity || payload.raw || null;
    regenerate = Boolean(payload.regenerate);
    options = payload.options || payload;
  } else {
    entityType = args[0];
    entityId = args[1];
    questionId = args[2] ?? null;
    context = args[3] || '';
    prompt = args[4] || '';
    tags = Array.isArray(args[5]) ? args[5] : [];
    source = args[6] || null;

    if (typeof args[7] === 'boolean') {
      regenerate = args[7];
      options = args[8] || {};
    } else {
      regenerate = Boolean(args[7]?.regenerate);
      options = args[7] || {};
    }
  }

  return {
    entityType,
    entityId,
    questionId,
    context,
    prompt,
    tags,
    source,
    regenerate,
    options,
  };
}

export async function createAIResponse(...args: any[]): Promise<AIContentCompat> {
  const {
    entityType,
    entityId,
    questionId,
    context,
    prompt,
    tags,
    source,
    regenerate,
    options,
  } = parseCreateArgs(args);

  const sessionId =
    options.session_id ||
    options.sessionId ||
    buildAIContentSessionId(entityType, entityId, questionId);

  const fallback: Record<string, any> = {
    id: sessionId,
    session_id: sessionId,
    entity_type: entityType,
    entity_id: entityId ?? null,
    related_type: entityType || 'ai-content',
    related_id: entityId ?? 'default',
    question: buildLegacyQuestion(questionId, options.title),
    question_id: questionId || null,
    prompt_text: prompt || context,
    request_text: prompt || context,
    status: 'complete',
  };

  const chatPayload = buildChatPayload({
    entityType,
    entityId,
    questionId,
    context,
    prompt,
    tags,
    source,
    options: {
      ...options,
      regenerate,
      metadata: {
        ...(options.metadata || {}),
        regenerate,
      },
    },
  });

  /**
   * POST /api/ai/chat/ is the only chat route. The former 'chat/' fallbacks
   * resolved to /api/chat/, which is served by something outside the AI service,
   * so a failure here used to re-post this payload to an unrelated endpoint.
   */
  const chatResponse = await tryPost('ai/chat/', chatPayload);

  if (chatResponse) {
    return toAIContentCompat(
      {
        ...chatResponse,
        session_id: valueOf(chatResponse.session_id, sessionId),
        conversation_id: stringId(valueOf(chatResponse.conversation_id, chatResponse.session_id)),
        message_id: stringId(
          valueOf(
            chatResponse.message_id,
            chatResponse.assistant_message_id,
            chatResponse.response_message_id,
          ),
        ),
        response: valueOf(
          chatResponse.response,
          chatResponse.answer,
          chatResponse.content,
          chatResponse.message,
        ),
      },
      fallback,
    );
  }

  if (context || prompt) {
    return toAIContentCompat(
      {
        ...fallback,
        response: context || prompt,
        content: context || prompt,
        response_text: context || prompt,
        status: 'complete',
      },
      fallback,
    );
  }

  throw new Error('No AI response could be created.');
}

export async function fetchOrCreateAIContent(
  entityType: string,
  entityId: ID,
  questionId?: ID | null,
  context?: string,
  prompt?: string,
  tags?: any[],
  source?: any,
  optionsOrRegenerate?: FetchOrCreateOptions | boolean,
  maybeOptions?: FetchOrCreateOptions,
): Promise<AIContentCompat> {
  const regenerate =
    typeof optionsOrRegenerate === 'boolean'
      ? optionsOrRegenerate
      : Boolean(optionsOrRegenerate?.regenerate);

  const options =
    typeof optionsOrRegenerate === 'boolean'
      ? maybeOptions || {}
      : optionsOrRegenerate || {};

  if (!regenerate) {
    const existing = await fetchAIResponse(entityType, entityId, questionId, context || '', options);

    if (existing?.response || existing?.content || existing?.response_text) {
      return existing;
    }
  }

  return createAIResponse(
    entityType,
    entityId,
    questionId,
    context || '',
    prompt || '',
    tags || [],
    source,
    regenerate,
    {
      ...options,
      regenerate,
      metadata: {
        ...(options.metadata || {}),
        regenerate,
      },
    },
  );
}

export async function fetchAIContext(...args: any[]): Promise<AIContentCompat> {
  let entityType = 'context';
  let entityId: ID = 'default';
  let source: any = null;
  let prompt = '';
  let options: FetchOrCreateOptions = {};

  if (args.length === 1 && args[0] && typeof args[0] === 'object') {
    const payload = args[0];

    entityType = payload.entity_type || payload.related_type || payload.type || 'context';
    entityId = payload.entity_id || payload.related_id || payload.id || 'default';
    source = payload.source || payload.entity || payload.customer || payload;
    prompt =
      payload.prompt ||
      payload.context_prompt ||
      payload.message ||
      payload.question ||
      'Build concise AI context for this record.';
    options = payload.options || payload;
  } else {
    entityType = args[0] || 'context';
    entityId = args[1] || 'default';
    source = args[2] || null;
    prompt = args[3] || 'Build concise AI context for this record.';
    options = args[4] || {};
  }

  const sourceText = stringifyValue(source, '');

  const contextPrompt = [
    prompt,
    sourceText
      ? `\n\nRecord context:\n${sourceText}`
      : '',
  ].join('');

  return fetchOrCreateAIContent(
    entityType,
    entityId,
    'context',
    contextPrompt,
    contextPrompt,
    [],
    source,
    true,
    {
      ...options,
      metadata: {
        ...(options.metadata || {}),
        source: 'fetchAIContext',
        regenerate: true,
      },
    },
  );
}

export async function updateAIResponse(
  recordOrId: AIContentCompat | CanonicalAIContentRecord | AIResponse | ID,
  patchOrResponse: Record<string, any> | string,
  feedback?: any,
): Promise<AIContentCompat> {
  const record =
    typeof recordOrId === 'object' && recordOrId !== null
      ? (recordOrId as Record<string, any>)
      : null;

  const id =
    typeof recordOrId === 'object' && recordOrId !== null
      ? valueOf(
        (recordOrId as any).message_id,
        (recordOrId as any).assistant_message_id,
        (recordOrId as any).ai_response_id,
        (recordOrId as any).id,
      )
      : recordOrId;

  const patch: Record<string, any> =
    typeof patchOrResponse === 'string'
      ? {
        response: patchOrResponse,
        content: patchOrResponse,
        response_text: patchOrResponse,
        feedback,
        metadata: {},
      }
      : {
        ...(patchOrResponse || {}),
        feedback: patchOrResponse?.feedback ?? feedback,
        metadata: patchOrResponse?.metadata || {},
      };

  if (!id) {
    return toAIContentCompat(
      {
        ...(record || {}),
        ...patch,
      },
      record || {},
    );
  }

  const nextContent = valueOf(
    patch.content,
    patch.response,
    patch.response_text,
    '',
  );

  /**
   * Try newer agent-history patch first. If unavailable, fall back to local
   * optimistic compatibility object so legacy AIContent editing does not fail.
   */
  try {
    const updated = await apiPatchJson<any>(`ai/history/message/${id}/`, {
      content: nextContent,
      response: nextContent,
      response_text: nextContent,
      feedback: patch.feedback,
      metadata: {
        ...(record?.metadata || {}),
        ...(patch.metadata || {}),
        feedback: patch.feedback,
      },
    });

    return toAIContentCompat(updated, {
      ...(record || {}),
      ...patch,
      id: String(id),
      message_id: stringId(id),
    });
  } catch {
    return toAIContentCompat(
      {
        ...(record || {}),
        ...patch,
        id: String(id),
        message_id: stringId(valueOf(record?.message_id, id)),
        response: nextContent,
        content: nextContent,
        response_text: nextContent,
        metadata: {
          ...(record?.metadata || {}),
          ...(patch.metadata || {}),
          feedback: patch.feedback,
        },
      },
      record || {},
    );
  }
}

export async function updateAIContentMessage(...args: any[]): Promise<AIContentCompat> {
  if (args.length === 1 && args[0] && typeof args[0] === 'object') {
    const payload = args[0];

    const id = valueOf(
      payload.message_id,
      payload.assistant_message_id,
      payload.ai_response_id,
      payload.id,
    );

    const content = valueOf(
      payload.content,
      payload.response,
      payload.response_text,
      '',
    );

    return updateAIResponse(
      {
        ...payload,
        id: String(id || payload.id || payload.session_id || 'ai-content'),
        message_id: stringId(id),
      },
      {
        ...payload,
        content,
        response: content,
        response_text: content,
        metadata: payload.metadata || {},
      },
    );
  }

  const recordOrId = args[0];
  const content = args[1];
  const metadata = args[2] || {};

  return updateAIResponse(recordOrId, {
    content,
    response: content,
    response_text: content,
    metadata,
  });
}

export async function submitAIFeedback(
  payloadOrRecord: FeedbackPayload | AIContentCompat | CanonicalAIContentRecord | AIResponse | ID,
  feedback?: any,
  extra: Record<string, any> = {},
): Promise<any> {
  const isSinglePayload =
    arguments.length === 1 &&
    payloadOrRecord &&
    typeof payloadOrRecord === 'object' &&
    (
      'text' in payloadOrRecord ||
      'score' in payloadOrRecord ||
      'related_type' in payloadOrRecord ||
      'metadata' in payloadOrRecord
    );

  if (isSinglePayload) {
    const payload = payloadOrRecord as FeedbackPayload;

    const messageId = valueOf(
      payload.message_id,
      payload.agent_message,
      payload.metadata?.message_id,
      payload.related_id,
      null,
    );

    return apiPostJson<any>('db/ai-feedback/', {
      ...payload,
      agent_message: payload.agent_message || messageId,
      message_id: payload.message_id || messageId,
      text: payload.text || stringifyValue(payload.feedback || payload.score, ''),
      feedback: valueOf(payload.feedback, payload.score, payload.text),
      metadata: {
        ...(payload.metadata || {}),
        score: payload.score,
        source: payload.source || payload.metadata?.source || 'AIContent',
      },
    });
  }

  const record =
    typeof payloadOrRecord === 'object' && payloadOrRecord !== null
      ? (payloadOrRecord as Record<string, any>)
      : null;

  const messageId =
    record
      ? valueOf(record.message_id, record.assistant_message_id, record.ai_response_id, record.id)
      : payloadOrRecord;

  const payload = {
    agent_message: messageId,
    message_id: messageId,
    conversation: record?.conversation_id || null,
    session_id: record?.session_id || null,

    related_type: record?.related_type || record?.entity_type || extra.related_type || null,
    related_id: record?.related_id || record?.entity_id || extra.related_id || null,

    feedback,
    text: typeof feedback === 'string' ? feedback : stringifyValue(feedback),
    metadata: {
      ...(record?.metadata || {}),
      ...extra,
      feedback,
      compatibility_mode: 'AIResponse',
    },
  };

  return apiPostJson<any>('db/ai-feedback/', payload);
}

export const fetchAIContent = fetchAIResponse;
export const saveAIFeedback = submitAIFeedback;

export default {
  buildAIContentSessionId,
  toAIContentCompat,

  fetchAIContext,

  fetchAIResponse,
  fetchAIContent,

  createAIResponse,
  fetchOrCreateAIContent,

  updateAIResponse,
  updateAIContentMessage,

  submitAIFeedback,
  saveAIFeedback,
};